import React, { ReactNode, useEffect, useMemo } from 'react';
import { Nullable } from 'global';
import { useDispatch } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { rejectDisabledFinancePrograms } from 'modules/financeProgram';
import { FinanceProgram, LenderProfile, Location } from '@trnsact/trnsact-shared-types';
import { ApolloQueryResult, LazyQueryExecFunction, OperationVariables, useLazyQuery, useQuery } from '@apollo/client';
import { deskingActions } from '../../model';
import { equipmentMileageFormatter } from '../../lib';
import { FinanceProgramFormValues } from '../../types';
import { equipmentDependentFields } from '../../constants';
import { GET_EQUIPMENT, GET_FINANCE_PROGRAMS, GET_PROPOSAL_MENUS, GET_PROPOSAL_PRODUCTS } from '../../api';
import {
  EquipmentsResponse,
  EquipmentsVariables,
  FinanceProgramsResponse,
  FinanceProgramsVariables,
  ProposalMenusResponse,
  ProposalProductsResponse,
} from '../../api/types';

interface RenderProps {
  getEquipmentDataById: LazyQueryExecFunction<EquipmentsResponse, EquipmentsVariables>;
  refetchProposalProducts: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<ProposalProductsResponse>>;
}

interface Props {
  accountId: string;
  location: Nullable<Location>;
  lenderProfiles: LenderProfile[];
  onFinanceProgramsLoaded: (financePrograms: FinanceProgram[]) => void;
  children: (renderProps: RenderProps) => ReactNode;
}

export const ApiContainer = ({ accountId, location, lenderProfiles, onFinanceProgramsLoaded, children }: Props) => {
  const dispatch = useDispatch();

  const { setValue, control } = useFormContext<FinanceProgramFormValues>();

  const [serialNumberVin, mileage, contactState, financeAmount, retailPrice, equipmentCondition, financeTerms] =
    useWatch({
      control,
      name: [
        'equipment.serialNumberVin',
        'equipment.mileage',
        'contact.state',
        'saleDetails.financeAmount',
        'equipment.retailPrice',
        'equipment.newUsed',
        'financeQuote.terms',
      ],
    });

  useQuery<ProposalMenusResponse>(GET_PROPOSAL_MENUS, {
    onCompleted(response) {
      if (!Array.isArray(response?.proposalMenus)) return;
      dispatch(deskingActions.setProposalMenus({ menuOptions: response.proposalMenus }));
    },
  });

  useQuery<FinanceProgramsResponse, FinanceProgramsVariables>(GET_FINANCE_PROGRAMS, {
    skip: !accountId,
    variables: { accountId },
    onCompleted(response) {
      if (!Array.isArray(response?.financePrograms)) return;
      const financePrograms = rejectDisabledFinancePrograms(response.financePrograms, lenderProfiles);
      onFinanceProgramsLoaded(financePrograms);
    },
  });

  const [getEquipmentDataById] = useLazyQuery<EquipmentsResponse, EquipmentsVariables>(GET_EQUIPMENT, {
    onCompleted(response) {
      const equipment = response?.equipments?.[0];

      setValue('equipment.selectedEquipment', equipment?.equipmentId);
      setValue(`equipment.mileage`, equipmentMileageFormatter(equipment?.mileage, 'toView').toString());

      equipmentDependentFields.forEach(field => setValue(`equipment.${field}`, equipment?.[field] ?? ''));

      dispatch(deskingActions.setCurrentEquipmentData({ id: equipment?.equipmentId }));
    },
  });

  const proposalProductsVariables = useMemo(
    () => ({
      input: {
        sourceOfCreation: ['base', 'manual'],
        proposalProductCriteria: {
          state: contactState,
          vin: serialNumberVin?.trim(),
          insuredAmount: financeAmount,
          locationId: location?.locationId,
          odometer: Number(equipmentMileageFormatter(mileage, 'toSimpleStr')),
          financeAmount: Number(financeAmount),
          equipmentRetailPrice: Number(equipmentMileageFormatter(retailPrice, 'toSimpleStr')),
          equipmentCondition,
          financeMonths: Array.isArray(financeTerms) && financeTerms.length > 0 ? Number(financeTerms[0]) : undefined,
        },
      },
    }),
    [
      contactState,
      serialNumberVin,
      financeAmount,
      mileage,
      location?.locationId,
      retailPrice,
      financeTerms,
      equipmentCondition,
    ]
  );

  const { refetch: refetchProposalProducts, loading } = useQuery<ProposalProductsResponse>(GET_PROPOSAL_PRODUCTS, {
    notifyOnNetworkStatusChange: true,
    variables: proposalProductsVariables,
    onCompleted(response) {
      if (!Array.isArray(response?.proposalProducts)) return;
      dispatch(
        deskingActions.setProducts({ products: response.proposalProducts, netFinanceAmount: Number(financeAmount) })
      );
    },
  });

  useEffect(() => {
    dispatch(deskingActions.toggleIsProposalProductsLoading({ isLoading: loading }));
  }, [dispatch, loading]);

  return <>{children({ getEquipmentDataById, refetchProposalProducts })}</>;
};
